import {
  Box, Flex, Text, Button, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, HStack, VStack,
} from "@chakra-ui/react";
import { useState } from "react";

import { MAX_USERS_TRIAL } from "./constants";
import { computeProrata, formatAmount } from "./helpers";
import {
  SubscriptionState,
} from "./types";
import { Card, ProgressBar } from "./UI";

interface UsersCardProps {
    sub: SubscriptionState;
    onAddUsers: (count: number) => void;
    onRemoveUsers: (count: number) => void;
}

export function UsersCard({ sub, onAddUsers, onRemoveUsers }: UsersCardProps) {
    const addModal    = useDisclosure();
    const removeModal = useDisclosure();

    const [usersToAdd, setUsersToAdd]       = useState<number>(1);
    const [usersToRemove, setUsersToRemove] = useState<number>(1);

    const currentPlan     = sub.plan;
    const totalUsers      = sub.usersAuthorized;
    const extraUsers      = Math.max(0, totalUsers - currentPlan.usersIncluded);
    const maxUsersAllowed = sub.isTrial ? MAX_USERS_TRIAL : 99;
    const canAddUsers     = totalUsers < maxUsersAllowed;
    const canRemoveUsers  = !sub.isTrial && extraUsers > 0;

    const daysRemainingInYear = Math.max(0, 365 - sub.trialDaysLeft);
    const prorataPerUser      = sub.isTrial ? 0 : computeProrata(currentPlan.pricePerExtraUser, daysRemainingInYear);

    function handleAddUsers(): void {
        onAddUsers(usersToAdd);
        setUsersToAdd(1);
        addModal.onClose();
    }

    function handleRemoveUsers(): void {
        onRemoveUsers(usersToRemove);
        setUsersToRemove(1);
        removeModal.onClose();
    }

    function openAddModal(): void {
        setUsersToAdd(1);
        addModal.onOpen();
    } 

    function openRemoveModal(): void {
        setUsersToRemove(1);
        removeModal.onOpen();
    }

    return (
        <>
            <Card>
                <Flex justify="space-between" align="center" mb={5}>
                    <Text fontFamily="heading" fontSize="lg" color="gray.900">Utilisateurs</Text>
                    <HStack spacing={2}>
                        {canRemoveUsers && (
                            <Button size="xs" variant="ghost" color="red.400" _hover={{ bg: "red.50" }} onClick={openRemoveModal}>
                                − Retirer
                            </Button>
                        )}
                        {canAddUsers && (
                            <Button size="xs" variant="outline" borderColor="brand.500" color="brand.500" _hover={{ bg: "brand.50" }} onClick={openAddModal}>
                                + Ajouter
                            </Button>
                        )}
                    </HStack>
                </Flex>

                <VStack spacing={4} align="stretch">
                    {sub.isTrial && (
                        <Box>
                            <Flex justify="space-between" mb={1}>
                                <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Sièges (essai)</Text>
                                <Text fontSize="sm" fontWeight="700" color="gray.800">
                                    {totalUsers}
                                    <Text as="span" fontSize="xs" color="gray.400" fontWeight="400"> / {MAX_USERS_TRIAL} max</Text>
                                </Text>
                            </Flex>
                            <ProgressBar value={totalUsers} max={MAX_USERS_TRIAL} color={currentPlan.color} />
                        </Box>
                    )}

                    <Box bg="surface.subtle" borderRadius="12px" p={4}>
                        <VStack spacing={2} align="stretch">
                            <Flex justify="space-between">
                                <Text fontSize="xs" color="gray.500">Inclus dans le plan</Text>
                                <Text fontSize="xs" fontWeight="600" color="gray.700">{currentPlan.usersIncluded}</Text>
                            </Flex>
                            {extraUsers > 0 && (
                                <Flex justify="space-between">
                                    <Text fontSize="xs" color="gray.500">Supplémentaires</Text>
                                    <Text fontSize="xs" fontWeight="600" color="gray.700">{extraUsers}</Text>
                                </Flex>
                            )}
                            <Divider borderColor="gray.200" />
                            <Flex justify="space-between" align="center">
                                <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Actifs</Text>
                                <HStack spacing={2}>
                                    <Box w="7px" h="7px" borderRadius="full" bg="green.400" />
                                    <Text fontSize="lg" fontWeight="700" color="gray.900">{sub.usersActive}</Text>
                                </HStack>
                            </Flex>
                        </VStack>
                    </Box>

                    {!sub.isTrial && extraUsers > 0 && (
                        <Box bg="orange.50" borderRadius="10px" p={3} border="1px solid" borderColor="orange.100">
                            <VStack spacing={1} align="start">
                                <Text fontSize="xs" fontWeight="600" color="orange.700">
                                    Suppression de sièges
                                </Text>
                                <Text fontSize="xs" color="orange.600" lineHeight="1.6">
                                    La réduction du nombre de sièges sera prise en compte au{" "}
                                    <Text as="span" fontWeight="700">renouvellement</Text> de votre abonnement.
                                    Aucun remboursement ne sera effectué pour la période en cours.
                                </Text>
                                <Text fontSize="xs" color="orange.600" lineHeight="1.6" mt={1}>
                                    ⚠️ Pensez également à{" "}
                                    <Text as="span" fontWeight="700">supprimer les comptes utilisateurs</Text>{" "}
                                    correspondants dans votre espace pour rester en accord avec le nombre de sièges défini.
                                </Text>
                            </VStack>
                        </Box>
                    )}

                    {sub.isTrial && !canAddUsers && (
                    <Box bg="orange.50" borderRadius="10px" p={3} border="1px solid" borderColor="orange.100">
                        <Text fontSize="xs" color="orange.700">
                            Nombre maximum de {MAX_USERS_TRIAL} utilisateurs atteint pendant la période d'essai.
                        </Text>
                    </Box>
                )}

                {!sub.isTrial && (
                    <Text fontSize="xs" color="gray.400" textAlign="center">
                        Utilisateur supplémentaire : {formatAmount(currentPlan.pricePerExtraUser)} / an
                    </Text>
                )}
            </VStack>
        </Card>

        <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} size="sm" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="20px" p={2}>
                <ModalHeader fontFamily="heading">Ajouter des utilisateurs</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="sm" fontWeight="500" color="gray.700" mb={3}>
                                Combien d'utilisateurs souhaitez-vous ajouter ?
                            </Text>
                            <Flex align="center" gap={4} justify="center">
                                <Button size="sm" variant="outline" borderColor="gray.200"
                                        isDisabled={usersToAdd <= 1}
                                        onClick={() => setUsersToAdd((n) => Math.max(1, n - 1))}>
                                    −
                                </Button>
                                <Text fontWeight="700" fontSize="2xl" color="gray.900" minW="40px" textAlign="center">
                                    {usersToAdd}
                                </Text>
                                <Button size="sm" variant="outline" borderColor="gray.200"
                                        isDisabled={totalUsers + usersToAdd >= maxUsersAllowed}
                                        onClick={() => setUsersToAdd((n) => n + 1)}>
                                    +
                                </Button>
                            </Flex>
                        </Box>

                        <Box borderRadius="12px" border="1px solid" p={4}
                             borderColor={sub.isTrial ? "green.100" : "brand.100"}
                             bg={sub.isTrial ? "green.50" : "brand.50"}>
                            <Flex justify="space-between" align="flex-start" mb={1}>
                                <Text fontSize="sm" fontWeight="600" color={sub.isTrial ? "green.700" : "brand.700"}>
                                    {sub.isTrial ? "Pendant l'essai" : "Facturation immédiate"}
                                </Text>
                                <Text fontSize="sm" fontWeight="800" color={sub.isTrial ? "green.700" : "brand.700"}>
                                    {sub.isTrial ? "Gratuit" : formatAmount(prorataPerUser * usersToAdd)}
                                </Text>
                            </Flex>
                            <Text fontSize="xs" color={sub.isTrial ? "green.600" : "brand.500"} lineHeight="1.6">
                                {sub.isTrial
                                    ? `À l'issue de l'essai, vous paierez le prorata de ${formatAmount(currentPlan.pricePerExtraUser)} / utilisateur / an.`
                                    : `Prorata jusqu'au renouvellement, puis ${formatAmount(currentPlan.pricePerExtraUser)} / utilisateur / an.`}
                            </Text>
                        </Box>

                        {sub.isTrial && (
                            <Text fontSize="xs" color="gray.400" textAlign="center">
                                Maximum {MAX_USERS_TRIAL} utilisateurs pendant l'essai
                                ({totalUsers + usersToAdd} / {MAX_USERS_TRIAL} après ajout)
                            </Text>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={addModal.onClose}>Annuler</Button>
                    <Button bg="brand.500" color="white" _hover={{ bg: "brand.600" }}
                            isDisabled={totalUsers + usersToAdd > maxUsersAllowed}
                            onClick={handleAddUsers}>
                        Confirmer l'ajout
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <Modal isOpen={removeModal.isOpen} onClose={removeModal.onClose} size="sm" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="20px" p={2}>
                <ModalHeader fontFamily="heading" color="gray.800">Retirer des utilisateurs</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="sm" fontWeight="500" color="gray.700" mb={3}>
                                Combien de sièges supplémentaires souhaitez-vous retirer ?
                            </Text>
                            <Flex align="center" gap={4} justify="center">
                                <Button size="sm" variant="outline" borderColor="gray.200"
                                        isDisabled={usersToRemove <= 1}
                                        onClick={() => setUsersToRemove((n) => Math.max(1, n - 1))}>
                                    −
                                </Button>
                                <Text fontWeight="700" fontSize="2xl" color="gray.900" minW="40px" textAlign="center">
                                    {usersToRemove}
                                </Text>
                                <Button size="sm" variant="outline" borderColor="gray.200"
                                        isDisabled={usersToRemove >= extraUsers}
                                        onClick={() => setUsersToRemove((n) => Math.min(extraUsers, n + 1))}>
                                    +
                                </Button>
                            </Flex>
                            <Text fontSize="xs" color="gray.400" textAlign="center" mt={2}>
                                Sièges après modification :{" "}
                                <Text as="span" fontWeight="700" color="gray.700">
                                    {totalUsers - usersToRemove}
                                </Text>
                            </Text>
                        </Box>

                        <Box bg="orange.50" borderRadius="12px" p={4} border="1px solid" borderColor="orange.100">
                            <Text fontSize="sm" fontWeight="600" color="orange.700" mb={1}>
                                Effectif au renouvellement
                            </Text>
                            <Text fontSize="xs" color="orange.600" lineHeight="1.6">
                                Cette modification ne prend pas effet immédiatement.
                                Vous continuerez à avoir accès aux {totalUsers} sièges actuels jusqu'à la prochaine
                                date de renouvellement.{" "}
                                <Text as="span" fontWeight="700">Aucun remboursement</Text> ne sera effectué.
                            </Text>
                        </Box>

                        <Box bg="red.50" borderRadius="12px" p={4} border="1px solid" borderColor="red.100">
                            <Text fontSize="sm" fontWeight="600" color="red.700" mb={1}>
                                Action requise de votre part
                            </Text>
                            <Text fontSize="xs" color="red.600" lineHeight="1.6">
                                Pensez à{" "}
                                <Text as="span" fontWeight="700">supprimer les comptes utilisateurs</Text>{" "}
                                correspondants dans votre espace. Sans cette action, des utilisateurs actifs
                                pourraient se retrouver sans accès lors du renouvellement, sans avertissement préalable.
                            </Text>
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={removeModal.onClose}>Annuler</Button>
                    <Button colorScheme="orange" onClick={handleRemoveUsers}>
                        Confirmer la réduction
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        </>
    );
}